import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Options Lifecycle — post-ACQUIRE position management (options E2E open
 * finding 2026-08-04: MTM / close / exercise / expire were API-only).
 *
 * Lists open option positions for a portfolio (GET
 * /api/derivatives/options/positions/{portfolioId}) with per-row actions:
 *   Mark      -> POST /api/derivatives/options/mtm            {positionId, optionPrice, underlyingPrice?}
 *   Close     -> POST /api/derivatives/options/close/{id}     {exitPrice, underlyingPrice?}
 *   Exercise  -> POST /api/derivatives/options/exercise/{id}  (long positions, settles intrinsic)
 *   Assign    -> POST /api/derivatives/options/assign/{id}    (short positions, pays intrinsic)
 * plus a panel-level expiry sweep (POST /expire). Every action posts a real
 * lifecycle trade through OptionsPostingSpi — bi-temporal supersede included.
 */

const panel: React.CSSProperties = {
  border: '1px solid rgba(148,163,184,0.18)', background: 'rgba(15,23,42,0.55)',
  borderRadius: 10, padding: '16px 20px', marginBottom: 16,
};
const label: React.CSSProperties = {
  color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
};
const th: React.CSSProperties = {
  ...label, textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid rgba(148,163,184,0.18)',
};
const td: React.CSSProperties = {
  color: '#e2e8f0', fontSize: 13, textAlign: 'right', padding: '8px 10px',
  borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap',
};
const input: React.CSSProperties = {
  background: '#0f172a', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 8,
  color: '#e2e8f0', padding: '8px 12px', fontSize: 13, width: '100%',
};
const btn = (tone: string): React.CSSProperties => ({
  background: tone === 'amber' ? 'rgba(245,158,11,0.15)' : tone === 'red' ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
  border: `1px solid ${tone === 'amber' ? 'rgba(245,158,11,0.4)' : tone === 'red' ? 'rgba(239,68,68,0.4)' : 'rgba(59,130,246,0.4)'}`,
  color: tone === 'amber' ? '#fbbf24' : tone === 'red' ? '#f87171' : '#60a5fa',
  borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 6,
});

const fmt = (v: any, digits = 2) =>
  v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });

type ModalState = { kind: 'mtm' | 'close' | 'exercise' | 'assign'; row: any } | null;

const OptionsLifecycleScreen: React.FC = () => {
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [portfolioId, setPortfolioId] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);
  const [risk, setRisk] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<ModalState>(null);
  const [price, setPrice] = useState('');
  const [underlying, setUnderlying] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    axios.get('/api/dimensions/portfolios')
      .then(r => setPortfolios(Array.isArray(r.data) ? r.data : (r.data?.content || [])))
      .catch(() => setPortfolios([]));
  }, []);

  const refresh = useCallback(async (pid: string) => {
    if (!pid) { setRows([]); setRisk(null); return; }
    try {
      const [pos, rk] = await Promise.all([
        axios.get(`/api/derivatives/options/positions/${pid}`),
        axios.get(`/api/derivatives/options/risk/${pid}`),
      ]);
      setRows(pos.data || []);
      setRisk(rk.data || null);
    } catch { setRows([]); setRisk(null); }
  }, []);

  useEffect(() => { refresh(portfolioId); }, [portfolioId, refresh]);

  const runAction = async (fn: () => Promise<any>, doneLabel: string) => {
    setBusy(true); setMsg(null);
    try {
      const res = await fn();
      const ok = res?.data?.success !== false;
      setMsg({ ok, text: res?.data?.message || res?.data?.error || doneLabel });
      await refresh(portfolioId);
      setModal(null); setPrice(''); setUnderlying('');
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  const submitModal = () => {
    if (!modal) return;
    const { kind, row } = modal;
    const underlyingParam = underlying === '' ? null : Number(underlying);
    if (kind === 'mtm') {
      runAction(() => axios.post('/api/derivatives/options/mtm', {
        positionId: row.positionId, optionPrice: Number(price), underlyingPrice: underlyingParam,
      }), 'Marked to market');
    } else if (kind === 'close') {
      runAction(() => axios.post(`/api/derivatives/options/close/${row.positionId}`, {
        exitPrice: Number(price), underlyingPrice: underlyingParam,
      }), 'Position closed');
    } else {
      const qs = underlyingParam != null ? `?underlyingPrice=${underlyingParam}` : '';
      runAction(() => axios.post(`/api/derivatives/options/${kind}/${row.positionId}${qs}`, {}),
        kind === 'exercise' ? 'Exercised' : 'Assigned');
    }
  };

  const needsPrice = modal?.kind === 'mtm' || modal?.kind === 'close';

  return (
    <div style={{ padding: '8px 4px', color: '#e2e8f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Options Lifecycle</div>
          <div style={{ color: '#94a3b8', fontSize: 13 }}>
            Mark, close, exercise and expire open option positions — every action posts a lifecycle trade to IBOR
          </div>
        </div>
        <button style={btn('amber')} disabled={busy || !portfolioId}
          onClick={() => runAction(() => axios.post('/api/derivatives/options/expire'), 'Expiry sweep complete')}>
          Run Expiry Sweep
        </button>
      </div>

      <div style={panel}>
        <div style={label}>Portfolio</div>
        <select style={{ ...input, maxWidth: 420, marginTop: 6 }} value={portfolioId}
          onChange={e => setPortfolioId(e.target.value)}>
          <option value="">Select portfolio…</option>
          {portfolios.map((p: any) => (
            <option key={p.id} value={p.id}>{p.portfolioName}</option>
          ))}
        </select>
      </div>

      {risk && (
        <div style={{ ...panel, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div><div style={label}>Open Positions</div><div style={{ fontWeight: 700, fontSize: 15 }}>{fmt(risk.openOptionPositions, 0)}</div></div>
          <div><div style={label}>Net Market Value</div><div style={{ fontWeight: 700, fontSize: 15 }}>${fmt(risk.netMarketValue)}</div></div>
          <div><div style={label}>Net Δ-Adj Exposure</div><div style={{ fontWeight: 700, fontSize: 15 }}>${fmt(risk.netDeltaAdjustedExposure)}</div></div>
          <div><div style={label}>Net Vega</div><div style={{ fontWeight: 700, fontSize: 15 }}>${fmt(risk.netVega)}</div></div>
          <div><div style={label}>Theta / Day</div><div style={{ fontWeight: 700, fontSize: 15 }}>${fmt(risk.netThetaDailyDecay)}</div></div>
          <div><div style={label}>Short Margin</div><div style={{ fontWeight: 700, fontSize: 15 }}>${fmt(risk.totalShortOptionMargin)}</div></div>
        </div>
      )}

      {msg && (
        <div style={{ ...panel, borderColor: msg.ok ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)',
          color: msg.ok ? '#4ade80' : '#f87171', fontSize: 13 }}>
          {msg.text}
        </div>
      )}

      <div style={{ ...panel, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Contract</th>
              <th style={th}>Contracts</th>
              <th style={th}>Strike</th>
              <th style={th}>Expiry</th>
              <th style={th}>Entry</th>
              <th style={th}>Mark</th>
              <th style={th}>Market Value</th>
              <th style={th}>Unrealized</th>
              <th style={th}>Δ-Adj Exposure</th>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#64748b', padding: 24 }}>
                {portfolioId ? 'No open option positions in this portfolio' : 'Select a portfolio'}
              </td></tr>
            )}
            {rows.map((r: any) => {
              const isLong = Number(r.contracts) > 0;
              return (
                <tr key={r.positionId}>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <div style={{ fontWeight: 600 }}>{r.contractSymbol}</div>
                    <div style={{ color: '#94a3b8', fontSize: 11 }}>
                      {r.underlyingSymbol} {r.optionType} · {isLong ? 'LONG' : 'SHORT'} · pos #{r.positionId}
                    </div>
                  </td>
                  <td style={td}>{fmt(r.contracts, 0)}</td>
                  <td style={td}>{fmt(r.strikePrice)}</td>
                  <td style={td}>{r.expiryDate ? String(r.expiryDate).slice(0, 10) : '—'}</td>
                  <td style={td}>{fmt(r.tradePrice, 4)}</td>
                  <td style={td}>{fmt(r.currentPrice, 4)}</td>
                  <td style={td}>${fmt(r.marketValue)}</td>
                  <td style={{ ...td, color: Number(r.unrealizedPnl) >= 0 ? '#4ade80' : '#f87171' }}>
                    ${fmt(r.unrealizedPnl)}
                  </td>
                  <td style={td}>${fmt(r.deltaAdjustedExposure)}</td>
                  <td style={{ ...td, textAlign: 'left', paddingLeft: 24 }}>
                    <button style={btn('blue')} disabled={busy}
                      onClick={() => { setModal({ kind: 'mtm', row: r }); setPrice(r.currentPrice != null ? String(r.currentPrice) : ''); setUnderlying(r.underlyingPrice != null ? String(r.underlyingPrice) : ''); }}>
                      Mark
                    </button>
                    <button style={btn('red')} disabled={busy}
                      onClick={() => { setModal({ kind: 'close', row: r }); setPrice(r.currentPrice != null ? String(r.currentPrice) : ''); setUnderlying(r.underlyingPrice != null ? String(r.underlyingPrice) : ''); }}>
                      Close
                    </button>
                    <button style={btn('amber')} disabled={busy}
                      onClick={() => { setModal({ kind: isLong ? 'exercise' : 'assign', row: r }); setPrice(''); setUnderlying(r.underlyingPrice != null ? String(r.underlyingPrice) : ''); }}>
                      {isLong ? 'Exercise' : 'Assign'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !busy && setModal(null)}>
          <div style={{ ...panel, width: 420, background: '#0f172a', marginBottom: 0 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, textTransform: 'capitalize' }}>
              {modal.kind === 'mtm' ? 'Mark to Market' : modal.kind} — {modal.row.contractSymbol}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 14 }}>
              {modal.kind === 'mtm' && 'Revalues MV / unrealized P&L and the Greeks. No cash moves.'}
              {modal.kind === 'close' && 'Posts a DISPOSE trade: settles closing proceeds to cash and realizes P&L vs premium.'}
              {(modal.kind === 'exercise' || modal.kind === 'assign') &&
                'Settles the option at intrinsic value against the underlying spot, flattens the position and realizes P&L.'}
            </div>
            {needsPrice && (
              <div style={{ marginBottom: 12 }}>
                <div style={label}>{modal.kind === 'mtm' ? 'Option Price* (per share)' : 'Exit Price* (per share)'}</div>
                <input style={{ ...input, marginTop: 6 }} type="number" value={price}
                  onChange={e => setPrice(e.target.value)} autoFocus />
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <div style={label}>Underlying Spot {needsPrice ? '(optional)' : '(optional — defaults to last mark)'}</div>
              <input style={{ ...input, marginTop: 6 }} type="number" value={underlying}
                onChange={e => setUnderlying(e.target.value)} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={{ ...btn('blue'), background: 'transparent', borderColor: 'rgba(148,163,184,0.3)', color: '#94a3b8' }}
                disabled={busy} onClick={() => setModal(null)}>Cancel</button>
              <button style={btn(modal.kind === 'close' ? 'red' : 'amber')}
                disabled={busy || (needsPrice && (price === '' || Number(price) < 0))}
                onClick={submitModal}>
                {busy ? 'Posting…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionsLifecycleScreen;
